/**
 * Lightweight modal dialog primitive (#44).
 *
 * Sprint-1 pragmatic version: built on top of the native `<dialog>` element
 * (HTML5) so we get a focus-trap, backdrop and Escape-to-close for free
 * without pulling in `@radix-ui/react-dialog`. Modern browsers all support
 * `showModal()` / `close()`; jsdom (used by Vitest) ships a polyfill via the
 * `HTMLDialogElement` interface, which is good enough for the unit tests.
 *
 * If we later need named slots / animated transitions / nested dialogs we
 * swap to Radix - consumers will only need to update imports because the
 * surface (`<Dialog open onOpenChange ... />`) matches the Radix API.
 */
import { X } from 'lucide-react';
import {
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  forwardRef,
  useEffect,
  useId,
  useRef,
} from 'react';

import { cn } from '@/lib/utils';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * When set, the backdrop and Escape are intercepted: the host can show a
   * confirmation before actually closing (e.g. "uploads still running").
   * The host MUST call `onOpenChange(false)` itself once it accepts the
   * close request.
   */
  onCloseRequest?: () => boolean;
  children: ReactNode;
  /** Accessible name for the dialog (read by screen readers). */
  ariaLabelledBy?: string;
}

export function Dialog({
  open,
  onOpenChange,
  onCloseRequest,
  children,
  ariaLabelledBy,
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  // Sync open/close with the imperative <dialog> API. We deliberately avoid
  // calling `showModal()` on the same render that mounts the element to give
  // jsdom (and React 18 strict-mode double-mount) a frame to attach.
  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;
    if (open && !node.open) {
      // jsdom polyfills `showModal`; the cast keeps the call typed even on
      // older dom typings that miss the method.
      const show = node.showModal as undefined | (() => void);
      if (typeof show === 'function') show.call(node);
      else node.setAttribute('open', '');
    } else if (!open && node.open) {
      node.close();
    }
  }, [open]);

  const handleBackdropClick = (event: MouseEvent<HTMLDialogElement>) => {
    event.stopPropagation();
    if (event.target === dialogRef.current) {
      requestClose();
    }
  };

  const requestClose = () => {
    if (onCloseRequest) {
      const allowed = onCloseRequest();
      if (!allowed) return;
    }
    onOpenChange(false);
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <dialog> handles Escape via onCancel; the onClick here only fires for backdrop clicks (no keyboard equivalent).
    <dialog
      ref={dialogRef}
      onCancel={(event) => {
        // Escape -> request close (might be intercepted by host).
        event.preventDefault();
        requestClose();
      }}
      onClick={handleBackdropClick}
      aria-labelledby={ariaLabelledBy}
      className={cn(
        // backdrop styled via the ::backdrop pseudo on the dialog itself in
        // the global stylesheet would be cleanest, but inline-keeps everything
        // in one file for the Sprint-1 closure.
        'rounded-lg border border-border bg-card text-card-foreground shadow-lg',
        'fixed inset-0 m-auto max-h-[90vh] w-[95vw] max-w-2xl p-0',
        'backdrop:bg-foreground/40 backdrop:backdrop-blur-sm',
      )}
    >
      {children}
    </dialog>
  );
}

export const DialogHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col gap-1 border-b border-border px-6 py-4', className)}
      {...props}
    />
  ),
);
DialogHeader.displayName = 'DialogHeader';

export const DialogTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2
      ref={ref}
      className={cn('text-lg font-semibold leading-tight tracking-tight', className)}
      {...props}
    />
  ),
);
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
DialogDescription.displayName = 'DialogDescription';

export const DialogBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-4 px-6 py-5', className)} {...props} />
  ),
);
DialogBody.displayName = 'DialogBody';

export const DialogFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col gap-2 border-t border-border px-6 py-4 sm:flex-row sm:items-center sm:justify-end',
        className,
      )}
      {...props}
    />
  ),
);
DialogFooter.displayName = 'DialogFooter';

interface DialogCloseButtonProps {
  onClose: () => void;
  /** Accessible label (defaults to German "Schließen"). */
  ariaLabel?: string;
}

export function DialogCloseButton({ onClose, ariaLabel = 'Schließen' }: DialogCloseButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClose}
      className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <X className="size-4" aria-hidden />
    </button>
  );
}

/** Convenience: a DOM id you can pass to `ariaLabelledBy`. */
export function useDialogTitleId(): string {
  return useId();
}
